/**
 * Inline CMS Editor Components
 * Minimizes clicks by enabling direct editing within cards
 */

import { useState } from 'react';
import { Edit2, Trash2, Plus, GripVertical, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import toast from 'react-hot-toast';

// ==================== Inline Text Input ====================
interface InlineTextProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onSave?: () => void;
  readonly label?: string;
  readonly placeholder?: string;
  readonly multiline?: boolean;
  readonly rows?: number;
}

export function InlineText({ 
  value, 
  onChange, 
  onSave, 
  label, 
  placeholder,
  multiline = false,
  rows = 3
}: InlineTextProps) {
  // Direct editing without edit mode - no individual save buttons
  return (
    <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      {label && <Label className="mb-2 text-slate-700 dark:text-slate-300 font-semibold text-xs uppercase tracking-wide">{label}</Label>}
      {multiline ? (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          className="w-full"
        />
      ) : (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full"
        />
      )}
    </div>
  );
}

// ==================== Inline Image Upload ====================
interface InlineImageProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onSave?: (newValue?: string) => void;
  readonly label?: string;
  readonly onUpload?: (file: File) => Promise<string>;
}

export function InlineImage({ value, onChange, onSave, label, onUpload }: InlineImageProps) {
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUpload) return;

    try {
      setUploading(true);
      const url = await onUpload(file);
      onChange(url);
      onSave?.(url);
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      {label && <Label className="mb-2 text-slate-700 dark:text-slate-300 font-semibold text-xs uppercase tracking-wide">{label}</Label>}
      
      {value && (
        <img src={value} alt={label} className="w-full h-32 object-cover rounded mb-2" />
      )}
{/*       
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter image URL or upload below"
        className="mb-2"
      /> */}
      
      {onUpload && (
        <div>
          <Label htmlFor={`file-upload-${label}`} className="cursor-pointer">
            <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-4 text-center hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-400 dark:hover:border-slate-500 transition-all duration-200">
              <Upload className="w-5 h-5 mx-auto mb-1 text-slate-400 dark:text-slate-500" />
              <span className="text-xs text-slate-600 dark:text-slate-400">
                {uploading ? <span className='text-red-600'>Uploading ...</span> : 'Click to upload image'}
              </span>
            </div>
          </Label>
          <input
            id={`file-upload-${label}`}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            disabled={uploading}
            className="hidden"
          />
        </div>
      )}
    </div>
  );
}

// ==================== Inline Array Editor ====================
interface ArrayItem {
  [key: string]: unknown;
}

interface InlineArrayProps<T extends ArrayItem> {
  readonly items: T[];
  readonly onChange: (items: T[]) => void;
  readonly onSave?: () => void;
  readonly onSaveItem?: (item: T, index: number) => void;
  readonly onDeleteItem?: (item: T, index: number) => Promise<boolean>;
  readonly label?: string;
  readonly renderItem: (item: T, index: number) => React.ReactNode;
  readonly renderForm: (
    item: Partial<T>,
    onChange: (field: keyof T, value: unknown) => void,
    onSave: () => void,
    onCancel: () => void
  ) => React.ReactNode;
  readonly getDefaultItem: () => T;
  readonly canReorder?: boolean;
  readonly canAdd?: boolean;
  readonly canDelete?: boolean;
}

export function InlineArray<T extends ArrayItem>({
  items,
  onChange,
  onSave,
  onSaveItem,
  onDeleteItem,
  label,
  renderItem,
  renderForm,
  getDefaultItem,
  canReorder = true,
  canAdd = true,
  canDelete = true,
}: InlineArrayProps<T>) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<Partial<T>>({});
  const [isAdding, setIsAdding] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleAdd = () => {
    setEditingItem(getDefaultItem());
    setIsAdding(true);
  };

  const handleEdit = (index: number) => {
    setEditingItem(items[index]);
    setEditingIndex(index);
  };

  const handleDelete = async (index: number) => {
    const performDelete = async () => {
      if (onDeleteItem) {
        // Call custom delete handler if provided
        const success = await onDeleteItem(items[index], index);
        if (success) {
          const newItems = items.filter((_, i) => i !== index);
          onChange(newItems);
        }
        // Toast is handled by onDeleteItem callback
      } else {
        // Default behavior - just remove from local state
        const newItems = items.filter((_, i) => i !== index);
        onChange(newItems);
        toast.success('Item removed');
        onSave?.();
      }
    };

    // Show confirmation toast with promise
    toast((t) => (
      <div className="flex flex-col gap-2">
        <p className="font-medium">Are you sure you want to delete this item?</p>
        <div className="flex gap-2 justify-end">
          <Button
            size="sm"
            variant="blueBg"
            onClick={() => toast.dismiss(t.id)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="red"
            onClick={() => {
              toast.dismiss(t.id);
              performDelete();
            }}
          >
            Delete
          </Button>
        </div>
      </div>
    ), {
      duration: 10000,
      position: 'top-center',
      style: {
        marginTop: '40vh',
      },
    });
  };

  const handleSaveNew = () => {
    const newItem = editingItem as T;
    const newItems = [...items, newItem];
    onChange(newItems);
    setIsAdding(false);
    setEditingItem({});
    // Call onSaveItem for new items too (with index being the last position)
    if (onSaveItem) {
      onSaveItem(newItem, items.length);
    } else {
      onSave?.();
    }
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;
    const newItems = [...items];
    const editedItem = editingItem as T;
    newItems[editingIndex] = editedItem;
    onChange(newItems);
    setEditingIndex(null);
    setEditingItem({});
    // Call onSaveItem with the edited item for immediate persistence
    if (onSaveItem) {
      onSaveItem(editedItem, editingIndex);
    } else {
      onSave?.();
    }
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setIsAdding(false);
    setEditingItem({});
  };

  const handleUpdateField = (field: keyof T, value: unknown) => {
    setEditingItem((prev) => {
      const updated = { ...prev, [field]: value };
      return updated;
    });
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.innerHTML);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(dropIndex, 0, draggedItem);
    
    onChange(newItems);
    setDraggedIndex(null);
    setDragOverIndex(null);
    onSave?.();
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const getItemClassName = (index: number) => {
    const baseClasses = 'group flex items-start gap-2 p-3 border-2 rounded-lg transition-all duration-200';
    const cursorClass = canReorder ? 'cursor-move' : '';
    
    if (draggedIndex === index) {
      return `${baseClasses} opacity-50 border-blue-400 dark:border-blue-500 ${cursorClass}`;
    }
    if (dragOverIndex === index) {
      return `${baseClasses} border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20 scale-105 ${cursorClass}`;
    }
    return `${baseClasses} border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-gradient-to-r hover:from-slate-50 hover:to-slate-100 dark:hover:from-slate-800 dark:hover:to-slate-850 ${cursorClass}`;
  };

  return (
    <div>
      {label && <Label className="mb-2">{label}</Label>}
      
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={`item-${index}-${JSON.stringify(item).substring(0, 30)}`}>
            {editingIndex === index ? (
              <Card className="p-4 border-2 border-slate-300 dark:border-slate-600 shadow-sm">
                {renderForm(editingItem, handleUpdateField, handleSaveEdit, handleCancel)}
              </Card>
            ) : (
              <div 
                draggable={canReorder}
                onDragStart={(e) => canReorder && handleDragStart(e, index)}
                onDragOver={(e) => canReorder && handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => canReorder && handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={getItemClassName(index)}
              >
                {canReorder && (
                  <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="h-5 w-5 p-0 flex items-center justify-center">
                      <GripVertical className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                )}
                
                <div className="flex-1">
                  {renderItem(item, index)}
                </div>
                
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(index)}
                    className="hover:bg-slate-200 dark:hover:bg-slate-700"
                  >
                    <Edit2 className="w-3 h-3" />
                  </Button>
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(index)}
                      className="hover:bg-red-100 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-3 h-3 text-red-600 dark:text-red-400" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {isAdding && (
        <Card className="p-4 mt-2 border-2 border-slate-300 dark:border-slate-600 shadow-sm">
          {renderForm(editingItem, handleUpdateField, handleSaveNew, handleCancel)}
        </Card>
      )}

      {canAdd && !isAdding && !editingIndex && (
        <Button variant="outline" size="sm" onClick={handleAdd} className="mt-2 w-full border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400">
          <Plus className="w-3 h-3 mr-1" /> Add {label}
        </Button>
      )}
    </div>
  );
}

// ==================== Section Card Wrapper ====================
interface SectionCardProps {
  readonly title: string;
  readonly description?: string;
  readonly icon?: React.ReactNode;
  readonly children: React.ReactNode;
  readonly onViewHistory?: () => void;
}

export function SectionCard({ title, description, icon, children, onViewHistory }: SectionCardProps) {
  return (
    <Card className="p-6 backdrop-blur-sm bg-white/95 dark:bg-slate-900/95 border-2 border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all duration-200">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          {icon && <div className="p-2 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 text-slate-700 dark:text-slate-300">{icon}</div>}
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{title}</h3>
            {description && <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{description}</p>}
          </div>
        </div>
        {/* {onViewHistory && (
          <Button variant="outline" size="sm" onClick={onViewHistory} className="border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800">
            View History
          </Button>
        )} */}
      </div>
      <div className="space-y-4">{children}</div>
    </Card>
  );
}
